import { useEffect, useState, useMemo } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/lib/auth-context";
import { PageHeader } from "@/components/PageHeader";
import { Plus, X, Calendar as CalIcon, Check, XCircle, Clock, CheckCircle2 } from "lucide-react";
import { fmtDateTime } from "@/lib/format";
import { toast } from "sonner";

const CATEGORY_LABEL = {
  maintenance: "Mantención",
  repair: "Reparación",
  service: "Servicio",
  others: "Otros",
};

const STATUS_META = {
  requested: { label: "Solicitada", cls: "bg-yellow-50 text-yellow-900 border-yellow-300" },
  accepted: { label: "Aceptada", cls: "bg-blue-50 text-blue-900 border-blue-300" },
  confirmed: { label: "Confirmada", cls: "bg-green-50 text-green-900 border-green-300" },
  rejected: { label: "Rechazada", cls: "bg-red-50 text-red-900 border-red-300" },
  cancelled: { label: "Cancelada", cls: "bg-gray-100 text-gray-700 border-gray-300" },
  completed: { label: "Completada", cls: "bg-black text-white border-black" },
};

export default function WorkshopCalendar() {
  const { user } = useAuth();
  const [slots, setSlots] = useState([]);
  const [bookings, setBookings] = useState([]);
  const [showSlotModal, setShowSlotModal] = useState(false);
  const [tab, setTab] = useState("upcoming");

  const loadAll = () => {
    api.get("/workshop-slots").then((r) => setSlots(r.data)).catch(() => {});
    api.get("/bookings").then((r) => setBookings(r.data)).catch(() => {});
  };
  useEffect(() => {
    loadAll();
  }, []);

  const canCreate = user.role === "workshop_admin" || user.role === "superadmin";

  const reqs = useMemo(() => bookings.filter((b) => b.status === "requested"), [bookings]);
  const others = useMemo(
    () => bookings.filter((b) => b.status !== "requested"),
    [bookings],
  );

  const act = async (id, path, body) => {
    try {
      await api.post(`/bookings/${id}/${path}`, body || {});
      toast.success("Acción realizada");
      loadAll();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  const reject = async (id) => {
    const reason = window.prompt("Motivo de rechazo (opcional):", "") || "";
    await act(id, "reject", { reason });
  };
  const cancel = async (id) => {
    const reason = window.prompt("Motivo de cancelación (opcional):", "") || "";
    await act(id, "cancel", { reason });
  };

  const deleteSlot = async (id) => {
    if (!window.confirm("¿Eliminar este cupo?")) return;
    try {
      await api.delete(`/workshop-slots/${id}`);
      toast.success("Cupo eliminado");
      loadAll();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  return (
    <div data-testid="calendar-page">
      <PageHeader
        tag="Agenda del Taller"
        title="Calendario y Reservas"
        description="Define tus cupos disponibles y gestiona las solicitudes de los Rent-a-Car."
        action={
          canCreate && (
            <button
              onClick={() => setShowSlotModal(true)}
              className="h-touch px-6 bg-black text-white text-sm font-bold uppercase tracking-wider hover:bg-blue-600 flex items-center gap-2"
              data-testid="new-slot-btn"
            >
              <Plus size={18} strokeWidth={2.5} /> Nuevo Cupo
            </button>
          )
        }
      />

      {/* Requests that need attention */}
      {reqs.length > 0 && (
        <div className="mb-8" data-testid="pending-requests">
          <h2 className="font-display text-2xl mb-3">Solicitudes pendientes ({reqs.length})</h2>
          <div className="border border-yellow-300 overflow-x-auto">
            <table className="w-full">
              <thead className="bg-yellow-50 border-b border-yellow-300">
                <tr>
                  <Th>Patente</Th>
                  <Th>Rent-a-Car</Th>
                  <Th>Inicio</Th>
                  <Th>Categoría</Th>
                  <Th>Tipo</Th>
                  <Th>Descripción</Th>
                  <Th></Th>
                </tr>
              </thead>
              <tbody>
                {reqs.map((b) => (
                  <tr key={b.id} className="border-b border-gray-200" data-testid={`req-row-${b.id}`}>
                    <td className="px-4 py-4 font-mono font-bold">{b.vehicle?.plate || b.vehicle_plate}</td>
                    <td className="px-4 py-4 text-sm">{b.rentacar?.name || "-"}</td>
                    <td className="px-4 py-4 text-sm">{fmtDateTime(b.proposed_start)}</td>
                    <td className="px-4 py-4 text-sm">{CATEGORY_LABEL[b.category] || b.category}</td>
                    <td className="px-4 py-4 text-xs font-bold uppercase">
                      {b.is_overflow ? (
                        <span className="text-orange-600">Sobre cupo</span>
                      ) : (
                        "Cupo fijo"
                      )}
                    </td>
                    <td className="px-4 py-4 text-sm truncate max-w-xs">{b.description}</td>
                    <td className="px-4 py-4 text-right space-x-2 whitespace-nowrap">
                      <button
                        onClick={() => act(b.id, "accept")}
                        className="inline-flex items-center gap-1 h-9 px-3 bg-black text-white text-xs font-bold uppercase hover:bg-blue-600"
                        data-testid={`accept-${b.id}`}
                      >
                        <Check size={14} strokeWidth={2.5} /> Aceptar
                      </button>
                      <button
                        onClick={() => reject(b.id)}
                        className="inline-flex items-center gap-1 h-9 px-3 border border-red-400 text-red-700 text-xs font-bold uppercase hover:bg-red-50"
                        data-testid={`reject-${b.id}`}
                      >
                        <XCircle size={14} strokeWidth={2.5} /> Rechazar
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Slots list */}
      <div className="mb-8">
        <h2 className="font-display text-2xl mb-3">Cupos definidos</h2>
        <div className="border border-gray-200 overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                <Th>Inicio</Th>
                <Th>Término</Th>
                <Th>Tipo</Th>
                <Th>Capacidad</Th>
                <Th>Reservados</Th>
                <Th>Sobre cupo</Th>
                <Th>Categoría</Th>
                <Th>Notas</Th>
                <Th></Th>
              </tr>
            </thead>
            <tbody>
              {slots.map((s) => (
                <tr key={s.id} className="border-b border-gray-200 hover:bg-gray-50" data-testid={`slot-row-${s.id}`}>
                  <td className="px-4 py-4 text-sm">{fmtDateTime(s.start_at)}</td>
                  <td className="px-4 py-4 text-sm">{fmtDateTime(s.end_at)}</td>
                  <td className="px-4 py-4 text-xs font-bold uppercase">
                    {s.slot_type === "block" ? "Bloque" : "Slot fijo"}
                  </td>
                  <td className="px-4 py-4 text-sm">{s.capacity}</td>
                  <td className="px-4 py-4 text-sm">
                    {s.booked_count}/{s.capacity}{" "}
                    {s.remaining === 0 && <span className="text-orange-600 text-xs font-bold">(lleno)</span>}
                  </td>
                  <td className="px-4 py-4 text-xs">{s.allow_overflow ? "Sí" : "No"}</td>
                  <td className="px-4 py-4 text-sm">{s.category ? CATEGORY_LABEL[s.category] : "Todas"}</td>
                  <td className="px-4 py-4 text-sm truncate max-w-[200px]">{s.notes || "-"}</td>
                  <td className="px-4 py-4 text-right">
                    <button
                      onClick={() => deleteSlot(s.id)}
                      className="text-xs font-bold uppercase tracking-wider text-red-600 underline"
                      data-testid={`delete-slot-${s.id}`}
                    >
                      Eliminar
                    </button>
                  </td>
                </tr>
              ))}
              {slots.length === 0 && (
                <tr>
                  <td colSpan={9} className="px-4 py-12 text-center text-sm text-gray-500">
                    Aún no has definido cupos. Crea uno para que los Rent-a-Car puedan reservar.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Other bookings */}
      <div>
        <div className="flex gap-2 mb-3 border-b border-gray-200">
          <TabBtn active={tab === "upcoming"} onClick={() => setTab("upcoming")} testId="tab-upcoming">
            Reservas activas
          </TabBtn>
          <TabBtn active={tab === "history"} onClick={() => setTab("history")} testId="tab-history">
            Historial
          </TabBtn>
        </div>
        <div className="border border-gray-200 overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                <Th>Patente</Th>
                <Th>Rent-a-Car</Th>
                <Th>Inicio</Th>
                <Th>Categoría</Th>
                <Th>Estado</Th>
                <Th></Th>
              </tr>
            </thead>
            <tbody>
              {others
                .filter((b) =>
                  tab === "upcoming"
                    ? ["accepted", "confirmed"].includes(b.status)
                    : ["completed", "rejected", "cancelled"].includes(b.status),
                )
                .map((b) => (
                  <tr key={b.id} className="border-b border-gray-200 hover:bg-gray-50" data-testid={`booking-row-${b.id}`}>
                    <td className="px-4 py-4 font-mono font-bold">{b.vehicle?.plate || b.vehicle_plate}</td>
                    <td className="px-4 py-4 text-sm">{b.rentacar?.name || "-"}</td>
                    <td className="px-4 py-4 text-sm">{fmtDateTime(b.proposed_start)}</td>
                    <td className="px-4 py-4 text-sm">{CATEGORY_LABEL[b.category] || b.category}</td>
                    <td className="px-4 py-4">
                      <StatusPill status={b.status} />
                    </td>
                    <td className="px-4 py-4 text-right space-x-2 whitespace-nowrap">
                      {b.status === "confirmed" && (
                        <button
                          onClick={() => act(b.id, "complete")}
                          className="inline-flex items-center gap-1 h-9 px-3 bg-black text-white text-xs font-bold uppercase hover:bg-blue-600"
                          data-testid={`complete-${b.id}`}
                        >
                          <CheckCircle2 size={14} strokeWidth={2.5} /> Completar
                        </button>
                      )}
                      {["accepted", "confirmed"].includes(b.status) && (
                        <button
                          onClick={() => cancel(b.id)}
                          className="inline-flex items-center gap-1 h-9 px-3 border border-red-400 text-red-700 text-xs font-bold uppercase hover:bg-red-50"
                          data-testid={`cancel-${b.id}`}
                        >
                          Cancelar
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              {others.filter((b) =>
                tab === "upcoming"
                  ? ["accepted", "confirmed"].includes(b.status)
                  : ["completed", "rejected", "cancelled"].includes(b.status),
              ).length === 0 && (
                <tr>
                  <td colSpan={6} className="px-4 py-12 text-center text-sm text-gray-500">
                    Sin reservas en esta vista.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {showSlotModal && (
        <NewSlotModal
          onClose={() => setShowSlotModal(false)}
          onDone={() => {
            setShowSlotModal(false);
            loadAll();
          }}
        />
      )}
    </div>
  );
}

function Th({ children }) {
  return <th className="text-left px-4 py-3 text-xs font-bold uppercase tracking-wider text-gray-700">{children}</th>;
}

function TabBtn({ active, onClick, children, testId }) {
  return (
    <button
      onClick={onClick}
      data-testid={testId}
      className={`px-4 py-2 text-xs font-bold uppercase tracking-wider border-b-4 ${
        active ? "border-black text-black" : "border-transparent text-gray-500 hover:text-black"
      }`}
    >
      {children}
    </button>
  );
}

export function StatusPill({ status }) {
  const m = STATUS_META[status] || { label: status, cls: "bg-gray-100" };
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-1 border text-xs font-bold uppercase tracking-wider ${m.cls}`}>
      <Clock size={12} strokeWidth={2.5} /> {m.label}
    </span>
  );
}

function NewSlotModal({ onClose, onDone }) {
  const [form, setForm] = useState({
    slot_type: "fixed",
    start_at: "",
    end_at: "",
    capacity: 1,
    category: "",
    allow_overflow: false,
    notes: "",
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const update = (k) => (e) => {
    const v = e.target.type === "checkbox" ? e.target.checked : e.target.value;
    setForm({ ...form, [k]: v });
  };

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const payload = {
        slot_type: form.slot_type,
        start_at: new Date(form.start_at).toISOString(),
        end_at: new Date(form.end_at).toISOString(),
        capacity: parseInt(form.capacity, 10) || 1,
        allow_overflow: !!form.allow_overflow,
        notes: form.notes,
      };
      if (form.category) payload.category = form.category;
      await api.post("/workshop-slots", payload);
      toast.success("Cupo creado");
      onDone();
    } catch (e) {
      setError(formatApiErrorDetail(e.response?.data?.detail));
    }
    setLoading(false);
  };

  return (
    <div className="fixed inset-0 bg-black/60 z-50 flex items-end md:items-center justify-center md:p-6">
      <div className="bg-white border-4 border-black w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        <div className="p-6 border-b border-gray-200 flex justify-between items-center">
          <h2 className="font-display text-2xl flex items-center gap-2">
            <CalIcon size={24} strokeWidth={2.5} /> Nuevo Cupo
          </h2>
          <button onClick={onClose} data-testid="close-slot-modal">
            <X size={24} strokeWidth={2.5} />
          </button>
        </div>
        <form onSubmit={submit} className="p-6 space-y-5" data-testid="new-slot-form">
          <div>
            <label className="veta-tag block mb-2">Tipo de cupo</label>
            <select
              value={form.slot_type}
              onChange={update("slot_type")}
              className="w-full h-touch px-3 border border-gray-300"
              data-testid="slot-type-select"
            >
              <option value="fixed">Slot fijo (horario puntual)</option>
              <option value="block">Bloque (ventana amplia con capacidad)</option>
            </select>
            <p className="text-xs text-gray-500 mt-1">
              {form.slot_type === "fixed"
                ? "Ej: 10:00 con capacidad 1 = un auto a esa hora."
                : "Ej: 09:00–13:00 con capacidad 3 = hasta 3 autos atendidos en paralelo."}
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="veta-tag block mb-2">Inicio</label>
              <input
                type="datetime-local"
                required
                value={form.start_at}
                onChange={update("start_at")}
                className="w-full h-touch px-3 border border-gray-300"
                data-testid="slot-start"
              />
            </div>
            <div>
              <label className="veta-tag block mb-2">Término</label>
              <input
                type="datetime-local"
                required
                value={form.end_at}
                onChange={update("end_at")}
                className="w-full h-touch px-3 border border-gray-300"
                data-testid="slot-end"
              />
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="veta-tag block mb-2">Capacidad (vehículos)</label>
              <input
                type="number"
                min={1}
                required
                value={form.capacity}
                onChange={update("capacity")}
                className="w-full h-touch px-3 border border-gray-300"
                data-testid="slot-capacity"
              />
            </div>
            <div>
              <label className="veta-tag block mb-2">Categoría (opcional)</label>
              <select
                value={form.category}
                onChange={update("category")}
                className="w-full h-touch px-3 border border-gray-300"
                data-testid="slot-category"
              >
                <option value="">Todas</option>
                <option value="maintenance">Mantención</option>
                <option value="repair">Reparación</option>
                <option value="service">Servicio</option>
                <option value="others">Otros</option>
              </select>
            </div>
          </div>
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="checkbox"
              checked={form.allow_overflow}
              onChange={update("allow_overflow")}
              className="w-5 h-5"
              data-testid="slot-overflow"
            />
            <span className="text-sm">
              Aceptar solicitudes <b>sobre cupo</b> en este horario (requieren mi aprobación explícita)
            </span>
          </label>
          <div>
            <label className="veta-tag block mb-2">Notas (opcional)</label>
            <textarea
              value={form.notes}
              onChange={update("notes")}
              rows={2}
              className="w-full px-3 py-3 border border-gray-300"
              data-testid="slot-notes"
            />
          </div>
          {error && <div className="status-danger px-4 py-3 text-sm">{error}</div>}
          <button
            type="submit"
            disabled={loading}
            className="w-full h-touch bg-black text-white text-sm font-bold uppercase tracking-wider hover:bg-blue-600 disabled:opacity-60"
            data-testid="save-slot-btn"
          >
            {loading ? "Guardando..." : "Crear Cupo"}
          </button>
        </form>
      </div>
    </div>
  );
}
